import { useState, useMemo, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import { MapPin, Check, ChevronsUpDown, Loader2, Navigation, Search, Hand, LocateFixed } from "lucide-react";
import { CL_REGIONS, CL_REGION_NAMES } from "@/lib/clRegions";

const pinIcon = L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-100%)"><svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg"><path d="M16 0C7.2 0 0 7.2 0 16c0 11 16 26 16 26s16-15 16-26C32 7.2 24.8 0 16 0z" fill="#7944e4"/><circle cx="16" cy="16" r="6" fill="#fff"/></svg></div>`,
  iconSize: [32, 42],
  iconAnchor: [16, 42],
});

function Recenter({ lat, lng, zoom }) {
  const map = useMap();
  useEffect(() => { if (lat && lng) map.setView([lat, lng], zoom); }, [lat, lng, zoom, map]);
  return null;
}

function ClickToPin({ onPick }) {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
}

const nominatim = async (q, limit = 1) => {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=${limit}&countrycodes=cl&q=${encodeURIComponent(q)}`,
    { headers: { "Accept-Language": "es" } });
  return res.json();
};

function RefSearch({ commune, region, onPick }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const run = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    try { setResults(await nominatim(`${q}, ${commune}, ${region}, Chile`, 5)); }
    catch { toast.error("No pudimos buscar. Intenta de nuevo."); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid="loc-ref-search">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} data-testid="loc-ref-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
            placeholder={`Busca un punto de referencia en ${commune}`} />
        </div>
        <Button type="button" variant="outline" onClick={run} disabled={busy} data-testid="loc-ref-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
        </Button>
      </div>
      {results && (
        <div className="rounded-lg border divide-y max-h-44 overflow-y-auto bg-background" data-testid="loc-ref-results">
          {results.length === 0 && <p className="p-2 text-xs text-muted-foreground">Sin resultados. Prueba con otro nombre (plaza, colegio, iglesia, supermercado).</p>}
          {results.map((r, i) => (
            <button key={r.place_id} type="button" data-testid={`loc-ref-result-${i}`}
              className="w-full text-left p-2 text-xs hover:bg-muted transition-colors"
              onClick={() => { onPick(parseFloat(r.lat), parseFloat(r.lon)); setResults(null); }}>
              {r.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function LocationPicker({ value, onChange }) {
  const { region, commune, address, address_reference, lat, lng } = value;
  const [comOpen, setComOpen] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [locating, setLocating] = useState(false);
  const [approx, setApprox] = useState(null);
  const markerRef = useRef(null);

  const communes = useMemo(() => (region ? CL_REGIONS[region] || [] : []), [region]);

  const set = (patch) => {
    const moved = ["region", "commune", "address"].some((k) => k in patch && patch[k] !== value[k]) && !("lat" in patch);
    if (moved) setApprox(null);
    onChange({ ...value, ...patch, ...(moved ? { lat: null, lng: null } : {}) });
  };
  const confirmPin = (la, ln) => { setApprox(null); set({ lat: la, lng: ln }); };

  const geocode = async () => {
    if (!address || !commune) { toast.error("Ingresa región, comuna y dirección primero"); return; }
    setGeocoding(true);
    try {
      const data = await nominatim(`${address}, ${commune}, ${region}, Chile`);
      if (data && data.length) {
        confirmPin(parseFloat(data[0].lat), parseFloat(data[0].lon));
        toast.success("Ubicación encontrada. Ajusta el pin si es necesario.");
        return;
      }
      let zone = await nominatim(`${commune}, ${region}, Chile`), zoom = 14, label = `la comuna de ${commune}`;
      if (!zone?.length) { zone = await nominatim(`${region}, Chile`); zoom = 9; label = `la ${region}`; }
      if (!zone?.length) { toast.error("No pudimos ubicar la zona. Intenta de nuevo."); return; }
      onChange({ ...value, lat: null, lng: null });
      setApprox({ lat: parseFloat(zone[0].lat), lng: parseFloat(zone[0].lon), zoom, label });
      toast.warning(`No encontramos la calle exacta. Te mostramos ${label}: marca tu taller en el mapa.`);
    } catch {
      toast.error("Error al geocodificar. Intenta de nuevo.");
    } finally { setGeocoding(false); }
  };

  const useGps = () => {
    if (!navigator.geolocation) { toast.error("Tu navegador no permite obtener la ubicación."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLocating(false);
        confirmPin(p.coords.latitude, p.coords.longitude);
        if (p.coords.accuracy > 50) toast.warning(`Ubicación aproximada (±${Math.round(p.coords.accuracy)} m). Revisa y ajusta el pin si es necesario.`);
        else toast.success("¡Listo! Pusimos el pin donde estás.");
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1
          ? "Permiso de ubicación denegado. Actívalo en la configuración del navegador (ícono del candado junto a la dirección web) y vuelve a intentarlo."
          : "No pudimos obtener tu ubicación. Sal a un lugar con mejor señal o activa el GPS.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };

  const onDragEnd = () => {
    const m = markerRef.current;
    if (m) { const p = m.getLatLng(); confirmPin(p.lat, p.lng); }
  };
  const pin = lat && lng ? { lat, lng, zoom: 16 } : approx;

  return (
    <div className="space-y-4" data-testid="location-picker">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>País</Label>
          <Input value="Chile" disabled data-testid="loc-country" />
        </div>
        <div>
          <Label>Región</Label>
          <Select value={region || ""} onValueChange={(v) => set({ region: v, commune: "" })}>
            <SelectTrigger data-testid="loc-region"><SelectValue placeholder="Selecciona región" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {CL_REGION_NAMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Comuna</Label>
        <Popover open={comOpen} onOpenChange={setComOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" disabled={!region} data-testid="loc-commune"
              className="w-full justify-between font-normal">
              {commune || (region ? "Busca o selecciona tu comuna" : "Primero elige una región")}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Escribe para buscar…" data-testid="loc-commune-search" />
              <CommandList>
                <CommandEmpty>Sin resultados.</CommandEmpty>
                <CommandGroup>
                  {communes.map((c) => (
                    <CommandItem key={c} value={c} data-testid={`loc-commune-${c}`}
                      onSelect={() => { set({ commune: c }); setComOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${commune === c ? "opacity-100" : "opacity-0"}`} />
                      {c}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div>
        <Label>Dirección (calle y número)</Label>
        <Input value={address || ""} onChange={(e) => set({ address: e.target.value })}
          data-testid="loc-address" placeholder="ej. Av. Providencia 1234" />
      </div>
      <div>
        <Label>Referencia <span className="text-muted-foreground font-normal">(opcional)</span></Label>
        <Input value={address_reference || ""} onChange={(e) => set({ address_reference: e.target.value })}
          data-testid="loc-reference" placeholder="ej. A tres cuadras de la plaza, casa esquina verde" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button type="button" variant="secondary" onClick={geocode} disabled={geocoding}
          className="w-full" data-testid="loc-geocode">
          {geocoding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}
          Ubicar en el mapa <span className="text-destructive ml-1">*</span>
        </Button>
        <Button type="button" variant="outline" onClick={useGps} disabled={locating}
          className="w-full" data-testid="loc-gps">
          {locating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LocateFixed className="w-4 h-4 mr-2 text-primary" />}
          Estoy en mi taller
        </Button>
      </div>
      {!(lat && lng) && !approx && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-2" data-testid="loc-map-required">
          Obligatorio: ubica tu dirección en el mapa. Así tus alumnos llegarán con Google Maps o Waze desde su ticket.
        </p>
      )}

      {pin && (
        <div className="space-y-2" data-testid="loc-map-wrap">
          {approx && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-2 flex gap-1.5" data-testid="loc-approx-banner">
              <Hand className="w-4 h-4 shrink-0" />
              <span>No encontramos la calle exacta, te mostramos {approx.label}. <strong>Toca el mapa o arrastra el pin hasta tu taller</strong> para confirmar el punto. Puedes buscar un lugar de referencia cercano.</span>
            </p>
          )}
          {commune && <RefSearch commune={commune} region={region} onPick={(la, ln) => setApprox({ lat: la, lng: ln, zoom: 17, label: "el punto de referencia" })} />}
          <div className="h-64 rounded-xl overflow-hidden border border-border">
            <MapContainer center={[pin.lat, pin.lng]} zoom={pin.zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[pin.lat, pin.lng]} icon={pinIcon} draggable eventHandlers={{ dragend: onDragEnd }} ref={markerRef} />
              <ClickToPin onPick={confirmPin} />
              <Recenter lat={pin.lat} lng={pin.lng} zoom={pin.zoom} />
            </MapContainer>
          </div>
          {lat && lng ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="loc-coords">
              <Navigation className="w-3.5 h-3.5" /> Revisa que el pin esté en el lugar exacto; si no, arrástralo o toca el mapa. Lat {lat.toFixed(5)}, Lng {lng.toFixed(5)}
            </p>
          ) : (
            <p className="text-xs text-destructive" data-testid="loc-pin-pending">Punto sin confirmar: toca el mapa o arrastra el pin para poder guardar.</p>
          )}
        </div>
      )}
    </div>
  );
}
